import type {DiffLine} from '../types'

/**
 * Provides a screen-reader only summary of the diff line
 * For each diff line, indicates whether there are conversations on both the original and modified lines.
 * @param leftLine the original line
 * @param rightLine the modified line
 * @param currentSide the side of the diff line that is currently being focused
 */
export default function DiffLineSummary({
  leftLine,
  rightLine,
  currentSide,
}: {
  leftLine: DiffLine
  rightLine: DiffLine
  currentSide: 'LEFT' | 'RIGHT'
}) {
  const leftLineThreadCount = leftLine.threadsData?.totalCount || 0
  const rightLineThreadCount = rightLine.threadsData?.totalCount || 0

  const firstLeftCommentAuthor = leftLine.threadsData?.threads?.[0]?.commentsData.comments?.[0]?.author?.login || ''
  const firstRightCommentAuthor = rightLine.threadsData?.threads?.[0]?.commentsData.comments?.[0]?.author?.login || ''

  let leftLineSummary = ''
  if (leftLineThreadCount === 1) {
    leftLineSummary = `has conversation started by @${firstLeftCommentAuthor}.`
  } else if (leftLineThreadCount > 1) {
    leftLineSummary = `has conversations.`
  }

  let rightLineSummary = ''
  if (rightLineThreadCount === 1) {
    rightLineSummary = `has conversation started by @${firstRightCommentAuthor}.`
  } else if (rightLineThreadCount > 1) {
    rightLineSummary = `has conversations.`
  }

  let summary = ''
  if (currentSide === 'LEFT') {
    const capitalizedLeftSummary = `${leftLineSummary.charAt(0).toUpperCase()}${leftLineSummary.slice(1)}`
    const rightLineSummaryWithModified = `${rightLineSummary.length > 1 ? 'Modified line' : ''} ${rightLineSummary}`
    summary = `${capitalizedLeftSummary} ${rightLineSummaryWithModified}`
  } else {
    const capitalizedRightSummary = `${rightLineSummary.charAt(0).toUpperCase()}${rightLineSummary.slice(1)}`
    const leftLineSummaryWithOriginal = `${leftLineSummary.length > 1 ? 'Original line' : ''} ${leftLineSummary}`
    summary = `${capitalizedRightSummary} ${leftLineSummaryWithOriginal}`
  }

  if (leftLineThreadCount === 0 && rightLineThreadCount === 0) {
    return null
  }

  return (
    <div className="sr-only" data-testid="pr-diffline-summary">
      {summary.trimStart().trimEnd()}
    </div>
  )
}

try{ DiffLineSummary.displayName ||= 'DiffLineSummary' } catch {}